/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    CashboxesByTaxNumbersDto,
    CashboxesByTaxNumbersItemDto,
    CompaniesListDto,
    CompaniesListRequestParams,
    CompanyDetailsDto,
    CompanyStoresListDto,
} from './companiesClient.typings';
import {
    companyDtoFactory,
    companyDetailsFactory,
    companyStoreFactory,
    turnoversByObjectFactory,
    cashboxesByTaxNumbersItemFactory,
} from './companiesFactories';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';
import { storeCashBoxesStatsFactory } from '../stores/storesFactories';
import { RegistryTurnoversByPeriodDto, RegistryTurnoversByObjectsDto } from '../apiCommon.typings';
import { TurnoversByPeriodFactory } from '../cashBoxes/cashBoxesFactories';

const COMPANIES_URL = /\/companies$/;
const COMPANIES_DETAILS_URL = /\/companies\/(\w+)$/;
const TAXPAYER_ACC_HOME_URL = /\/companies\/home/;
const COMPANIES_STORES_URL = /\/companies\/(\w+)\/stores/;
const COMPANIES_CASHBOXES_STATS: RegExp = /\/companies\/(\w+)\/cashboxes\/stats/;
const COMPANIES_GET_TURNOVERS_BY_PERIOD: RegExp = /\/companies\/\w+\/turnovers/;
const COMPANIES_GET_TURNOVERS_BY_OBJECTS: RegExp = /\/companies\/\w+\/stores\/turnovers/;
const CASHBOXES_BY_TAX_NUMBERS = /\/1.0.0\/companies\/cashboxes$/;

class CompaniesFeatureMock extends BaseFeatureMock {
    // TAXPAYER_ACC_HOME_URL (`/companies/home`) also matches the anchored
    // COMPANIES_DETAILS_URL (`/\/companies\/(\w+)$/`), and COMPANIES_STORES_URL
    // (`/\/companies\/(\w+)\/stores/`) would also match COMPANIES_GET_TURNOVERS_BY_OBJECTS's
    // url (`/companies/123/stores/turnovers`) since it's unanchored. Since axios-mock-adapter
    // matches in first-registered-first-matched order, the more specific routes below must stay
    // declared before the more generic ones they'd otherwise be shadowed by.
    public readonly taxpayerAccHomeDetails = this.route<CompanyDetailsDto>({
        method: 'get',
        url: TAXPAYER_ACC_HOME_URL,
        defaultResponse: () => {
            const data: CompanyDetailsDto = companyDetailsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly turnoversByObjects = this.route<RegistryTurnoversByObjectsDto>({
        method: 'get',
        url: COMPANIES_GET_TURNOVERS_BY_OBJECTS,
        defaultResponse: () => {
            const data: RegistryTurnoversByObjectsDto = {
                count: 200,
                turnovers: turnoversByObjectFactory.list({ count: 20 }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 1500,
    });

    public readonly companyStores = this.route<CompanyStoresListDto>({
        method: 'get',
        url: COMPANIES_STORES_URL,
        defaultResponse: (config) => {
            const data: CompanyStoresListDto = {
                count: 60,
                list: companyStoreFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly turnoversByPeriod = this.route<RegistryTurnoversByPeriodDto[]>({
        method: 'get',
        url: COMPANIES_GET_TURNOVERS_BY_PERIOD,
        defaultResponse: () => {
            const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
            TurnoversByPeriodFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 1500,
    });

    public readonly companyCashboxesStats = this.route<CashBoxesStats>({
        method: 'get',
        url: COMPANIES_CASHBOXES_STATS,
        defaultResponse: () => {
            const data: CashBoxesStats = storeCashBoxesStatsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly cashboxesByTaxNumbers = this.route<CashboxesByTaxNumbersDto>({
        method: 'post',
        url: CASHBOXES_BY_TAX_NUMBERS,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);

            const cashboxes: CashboxesByTaxNumbersItemDto[] = cashboxesByTaxNumbersItemFactory.list({
                count: body.limit,
            });

            let updatedCashboxes: CashboxesByTaxNumbersItemDto[];
            if (body.cashBoxTaxNumbers && body.cashBoxTaxNumbers.length !== 0) {
                updatedCashboxes = cashboxes.map((item, index) => {
                    return { ...item, taxNumber: body.cashBoxTaxNumbers[index] };
                });
            }
            if (body.deviceFactoryNumbers && body.deviceFactoryNumbers.length !== 0) {
                updatedCashboxes = cashboxes.map((item, index) => {
                    return { ...item, deviceFactoryNumber: body.deviceFactoryNumbers[index] };
                });
                updatedCashboxes.splice(0, 1);
            }

            const data: CashboxesByTaxNumbersDto = {
                afterKeyTaxNumber: 'someAfterKey',
                cashBoxes: updatedCashboxes ? updatedCashboxes : cashboxes,
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly companyDetails = this.route<CompanyDetailsDto>({
        method: 'get',
        url: COMPANIES_DETAILS_URL,
        defaultResponse: () => {
            const data: CompanyDetailsDto = companyDetailsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly listCompanies = this.route<CompaniesListDto, CompaniesListRequestParams>({
        method: 'post',
        url: COMPANIES_URL,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: CompaniesListDto = {
                afterKey: '234318932',
                companies: companyDtoFactory.list({ count: body.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    protected onReset(): void {
        companyDtoFactory.resetCount();
        companyDetailsFactory.resetCount();
        companyStoreFactory.resetCount();
        turnoversByObjectFactory.resetCount();
        cashboxesByTaxNumbersItemFactory.resetCount();
        storeCashBoxesStatsFactory.resetCount();
    }
}

export const companiesMock = new CompaniesFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addCompanyMockApi(mockAdapter: MockAdapter): void {
    companiesMock.register(mockAdapter, { delay: true });
}
